import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  IconButton,
  Divider,
  Collapse,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { 
  MAX_PRICE_DEFAULT, 
  DEFAULT_PRICE_RANGE, 
  sortSizes, 
  CATEGORY_FILTER_BLOCKLIST, 
  HIDDEN_CATEGORY_MARKERS 
} from "../../utils/filterUtils";

const FilterSidebar = ({
  open,
  onClose,
  filterOptions,
  activeFilters,
  onApply,
  onClear,
  type, // "new_arrivals" or "on_sale"
  brandName,
}) => {
  // Theme colors
  const themeColor = "#FF6B00";
  const themeGradient = "linear-gradient(135deg, #FF8C33 0%, #FF6B00 100%)";
  const themeBgLight = "rgba(255, 107, 0, 0.06)";
  const themeBgMedium = "rgba(255, 107, 0, 0.12)";

  // Local state for filter selections (applied on "Apply" click)
  const [localPriceRange, setLocalPriceRange] = useState(DEFAULT_PRICE_RANGE);
  const [localShowNullPriceOnly, setLocalShowNullPriceOnly] = useState(false);
  const [localCategories, setLocalCategories] = useState([]);
  const [localVariants, setLocalVariants] = useState({});

  // Section collapse state (default all closed)
  const [sectionsOpen, setSectionsOpen] = useState({});

  // Dynamic price limits from backend
  const minPriceLimit = Math.floor(filterOptions?.min_price || 0);
  let maxPriceLimit = Math.ceil(filterOptions?.max_price || MAX_PRICE_DEFAULT);
  if (maxPriceLimit <= minPriceLimit) {
    maxPriceLimit = minPriceLimit + 1000;
  }

  // Sync local state with active filters when sidebar opens
  useEffect(() => {
    if (open) {
      setLocalPriceRange(activeFilters?.priceRange || [minPriceLimit, maxPriceLimit]);
      setLocalShowNullPriceOnly(activeFilters?.showNullPriceOnly || false);
      setLocalCategories(activeFilters?.categories || []);
      setLocalVariants(activeFilters?.variants || {});

    }
  }, [open, filterOptions, activeFilters]);

  const toggleSection = (section) => {
    setSectionsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCategoryToggle = (category) => {
    setLocalCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleVariantToggle = (variantKey, variantValue) => {
    setLocalVariants((prev) => {
      const current = prev[variantKey] || [];
      const updated = current.includes(variantValue)
        ? current.filter((v) => v !== variantValue)
        : [...current, variantValue];
      return { ...prev, [variantKey]: updated };
    });
  };

  const handleApply = () => {
    onApply({
      priceRange: localPriceRange,
      showNullPriceOnly: localShowNullPriceOnly,
      categories: localCategories,
      variants: localVariants,
    });
    onClose();
  };

  const handleClear = () => {
    setLocalPriceRange([minPriceLimit, maxPriceLimit]);
    setLocalShowNullPriceOnly(false);
    setLocalCategories([]);
    setLocalVariants({});
    onClear();
    onClose();
  };

  const hasAnyFilterOption =
    filterOptions &&
    (filterOptions.categories?.length > 0 ||
      (filterOptions.variants && Object.keys(filterOptions.variants).length > 0) ||
      filterOptions.min_price !== undefined);

  // Section header component
  const SectionHeader = ({ title, section, count }) => (
    <Box
      onClick={() => toggleSection(section)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        py: 1.5,
        px: 0,
        "&:hover": { opacity: 0.8 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: "13px",
            color: "#333",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          {title}
        </Typography>
        {count > 0 && (
          <Box
            sx={{
              background: themeGradient,
              color: "#fff",
              fontSize: "10px",
              fontFamily: "Poppins-Bold",
              borderRadius: "10px",
              minWidth: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
            }}
          >
            {count}
          </Box>
        )}
      </Box>
      {sectionsOpen[section] ? (
        <ExpandLessIcon sx={{ fontSize: 20, color: "#888" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 20, color: "#888" }} />
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "360px" },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          background: "#FAFBFC",
          borderRight: "none",
          boxShadow: "8px 0 40px rgba(0,0,0,0.08)",
        },
      }}
      SlideProps={{
        timeout: { enter: 350, exit: 250 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          borderBottom: `2px solid ${themeColor}15`,
          background: `linear-gradient(180deg, ${themeBgLight} 0%, transparent 100%)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FilterListIcon sx={{ color: themeColor, fontSize: 22 }} />
          <Typography
            sx={{
              fontFamily: "Poppins-Bold",
              fontSize: "18px",
              color: "#1a1a1a",
              letterSpacing: "-0.01em",
            }}
          >
            Filters
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#999",
            "&:hover": { background: themeBgMedium, color: themeColor },
            transition: "all 0.2s ease",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Filter Content */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          py: 1,
          pb: "100px", // space for absolute footer
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: `${themeColor}40`,
            borderRadius: "4px",
          },
        }}
      >
        {!hasAnyFilterOption ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              opacity: 0.5,
            }}
          >
            <FilterListIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
            <Typography
              sx={{
                fontFamily: "Poppins-Medium",
                fontSize: "14px",
                color: "#999",
                textAlign: "center",
              }}
            >
              No filters available yet
            </Typography>
          </Box>
        ) : (
          <>
            {/* Price Range - Always visible */}
            <>
              <SectionHeader
                title="Price Range"
                section="price"
                count={
                  localShowNullPriceOnly || localPriceRange[0] > minPriceLimit || localPriceRange[1] < maxPriceLimit
                    ? 1
                    : 0
                }
              />
              <Collapse in={sectionsOpen.price} timeout={300}>
                <Box sx={{ px: 1, pb: 2, opacity: localShowNullPriceOnly ? 0.5 : 1, pointerEvents: localShowNullPriceOnly ? 'none' : 'auto' }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    <TextField
                      size="small"
                      value={localPriceRange[0]}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setLocalPriceRange([val, localPriceRange[1]]);
                      }}
                      inputProps={{
                        style: {
                          fontFamily: "Poppins-Medium",
                          fontSize: "13px",
                          textAlign: "center"
                        }
                      }}
                      sx={{ width: '45%' }}
                    />
                    <Typography sx={{ color: "#999" }}>-</Typography>
                    <TextField
                      size="small"
                      value={localPriceRange[1]}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setLocalPriceRange([localPriceRange[0], val]);
                      }}
                      inputProps={{
                        style: {
                          fontFamily: "Poppins-Medium",
                          fontSize: "13px",
                          textAlign: "center"
                        }
                      }}
                      sx={{ width: '45%' }}
                    />
                  </Box>
                  <Slider
                    value={localPriceRange}
                    onChange={(_, newValue) => setLocalPriceRange(newValue)}
                    min={minPriceLimit}
                    max={maxPriceLimit}
                    step={500}
                    valueLabelDisplay="off"
                    sx={{
                      color: themeColor,
                      height: 4,
                      "& .MuiSlider-thumb": {
                        width: 18,
                        height: 18,
                        backgroundColor: "#fff",
                        border: `2px solid ${themeColor}`,
                        boxShadow: `0 2px 8px ${themeColor}40`,
                        "&:hover, &.Mui-focusVisible": {
                          boxShadow: `0 0 0 6px ${themeColor}20`,
                        },
                      },
                      "& .MuiSlider-track": {
                        background: themeGradient,
                        border: "none",
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: "#E8E8E8",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ px: 1, pb: 2, pt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localShowNullPriceOnly}
                        onChange={(e) => setLocalShowNullPriceOnly(e.target.checked)}
                        size="small"
                        sx={{
                          color: "#ccc",
                          "&.Mui-checked": { color: themeColor },
                          transition: "all 0.2s ease",
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Poppins-Medium",
                          fontSize: "13px",
                          color: localShowNullPriceOnly ? "#333" : "#666",
                          transition: "color 0.2s ease",
                        }}
                      >
                        Price Unavailable
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Collapse>
              <Divider sx={{ borderColor: "rgba(0,0,0,0.04)" }} />
            </>

            {/* Category Filter */}
            {filterOptions.categories?.length > 0 && !CATEGORY_FILTER_BLOCKLIST.includes(brandName) && (
              <>
                <SectionHeader
                  title="Category"
                  section="category"
                  count={localCategories.length}
                />
                <Collapse in={sectionsOpen.category} timeout={300}>
                  <FormGroup sx={{ pb: 2 }}>
                    {filterOptions.categories
                      .filter(cat => !HIDDEN_CATEGORY_MARKERS.includes(cat.trim().toLowerCase()))
                      .map((cat) => (
                      <FormControlLabel
                        key={cat}
                        control={
                          <Checkbox
                            checked={localCategories.includes(cat)}
                            onChange={() => handleCategoryToggle(cat)}
                            size="small"
                            sx={{
                              color: "#ccc",
                              "&.Mui-checked": { color: themeColor },
                              transition: "all 0.2s ease",
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Poppins-Regular",
                              fontSize: "13px",
                              color: localCategories.includes(cat)
                                ? "#333"
                                : "#666",
                              transition: "color 0.2s ease",
                            }}
                          >
                            {cat}
                          </Typography>
                        }
                        sx={{
                          mx: 0,
                          py: 0.2,
                          px: 1,
                          borderRadius: "8px",
                          "&:hover": { background: themeBgLight },
                          transition: "background 0.2s ease",
                        }}
                      />
                    ))}
                  </FormGroup>
                </Collapse>
                <Divider sx={{ borderColor: "rgba(0,0,0,0.04)" }} />
              </>
            )}

            {/* Dynamic Variants */}
            {filterOptions.variants && Object.entries(filterOptions.variants).map(([vKey, vValues]) => {
              if (!vValues || vValues.length === 0) return null;

              const sectionName = `variant_${vKey}`;
              const activeCount = (localVariants[vKey] || []).length;

              // Special display for sizes
              const isSize = vKey.toLowerCase() === 'size';
              const displayValues = isSize ? sortSizes([...vValues]) : vValues;

              return (
                <React.Fragment key={vKey}>
                  <SectionHeader
                    title={vKey.charAt(0).toUpperCase() + vKey.slice(1)}
                    section={sectionName}
                    count={activeCount}
                  />
                  <Collapse in={sectionsOpen[sectionName]} timeout={300}>
                    {isSize ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          pb: 2,
                        }}
                      >
                        {displayValues.map((val) => {
                          const isSelected = (localVariants[vKey] || []).includes(val);
                          return (
                            <Box
                              key={val}
                              onClick={() => handleVariantToggle(vKey, val)}
                              sx={{
                                px: 1.5,
                                py: 0.6,
                                borderRadius: "8px",
                                border: `1.5px solid ${isSelected ? themeColor : "#E0E0E0"
                                  }`,
                                background: isSelected ? themeBgMedium : "#fff",
                                cursor: "pointer",
                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  borderColor: themeColor,
                                  background: themeBgLight,
                                  transform: "scale(1.02)",
                                },
                                "&:active": {
                                  transform: "scale(0.97)",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Poppins-Medium",
                                  fontSize: "12px",
                                  color: isSelected ? themeColor : "#555",
                                  transition: "color 0.2s ease",
                                  userSelect: "none",
                                }}
                              >
                                {val}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <FormGroup sx={{ pb: 2 }}>
                        {displayValues.map((val) => {
                          const isSelected = (localVariants[vKey] || []).includes(val);
                          return (
                            <FormControlLabel
                              key={val}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleVariantToggle(vKey, val)}
                                  size="small"
                                  sx={{
                                    color: "#ccc",
                                    "&.Mui-checked": { color: themeColor },
                                    transition: "all 0.2s ease",
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins-Regular",
                                    fontSize: "13px",
                                    color: isSelected ? "#333" : "#666",
                                    transition: "color 0.2s ease",
                                  }}
                                >
                                  {val}
                                </Typography>
                              }
                              sx={{
                                mx: 0,
                                py: 0.2,
                                px: 1,
                                borderRadius: "8px",
                                "&:hover": { background: themeBgLight },
                                transition: "background 0.2s ease",
                              }}
                            />
                          );
                        })}
                      </FormGroup>
                    )}
                  </Collapse>
                  <Divider sx={{ borderColor: "rgba(0,0,0,0.04)" }} />
                </React.Fragment>
              );
            })}
          </>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          px: 3,
          pt: 2.5,
          pb: { xs: "calc(24px + env(safe-area-inset-bottom, 24px))", sm: 2.5 },
          borderTop: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          gap: 1.5,
          background: "#fff",
          zIndex: 10,
        }}
      >
        <Button
          onClick={handleClear}
          variant="outlined"
          fullWidth
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: "13px",
            textTransform: "none",
            borderRadius: "12px",
            borderColor: "#ddd",
            color: "#666",
            py: 1.2,
            "&:hover": {
              borderColor: "#bbb",
              background: "rgba(0,0,0,0.02)",
            },
            transition: "all 0.2s ease",
          }}
        >
          Clear All
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          fullWidth
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: "13px",
            textTransform: "none",
            borderRadius: "12px",
            background: themeGradient,
            color: "#fff",
            py: 1.2,
            boxShadow: `0 4px 16px ${themeColor}40`,
            "&:hover": {
              boxShadow: `0 6px 24px ${themeColor}60`,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterSidebar;
